import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import {
  Building2,
  Mail,
  User,
  Lock,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import Swal from "sweetalert2";

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    password: "",
    password_confirmation: "",
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  // Solo necesitamos la URL del API para las peticiones fetch
  const getApiUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000/api";
  };

  const validateToken = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/test-invitation/${token}`);

      if (response.ok) {
        const data = await response.json();

        const expiresAt = new Date(data.invitation.expires_at);
        const now = new Date();

        if (now > expiresAt) {
          setError("Esta invitación ha expirado. Por favor, contacta al administrador.");
          return;
        }

        if (data.invitation.status === "accepted") {
          setError("Esta invitación ya ha sido aceptada.");
          return;
        }

        setInvitation(data);
      } else {
        setError("Invitación no válida o no encontrada.");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Error al validar la invitación.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password.length < 8) {
      Swal.fire("Error", "La contraseña debe tener al menos 8 caracteres", "error");
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      Swal.fire("Error", "Las contraseñas no coinciden", "error");
      return;
    }

    setValidating(true);

    try {
      const response = await fetch(`${getApiUrl()}/accept-invitation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          token: token,
          password: formData.password,
          password_confirmation: formData.password_confirmation,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        await Swal.fire({
          icon: "success",
          title: "¡Cuenta creada!",
          html: `
            <p>Tu cuenta ha sido creada exitosamente.</p>
            <p class="mt-2"><strong>Email:</strong> ${invitation.invitation.email}</p>
            <p class="text-sm text-gray-600 mt-3">Redirigiendo al inicio de sesión...</p>
          `,
          timer: 2000,
          showConfirmButton: false,
        });

        // ✅ USAMOS NAVIGATE PARA REDIRECCIÓN INTERNA
        navigate("/login");
      } else {
        throw new Error(result.message || "Error al aceptar invitación");
      }
    } catch (err) {
      console.error("Error:", err);
      Swal.fire("Error", err.message, "error");
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-6">
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-600">Validando invitación...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <CardTitle className="text-xl text-red-900">Invitación No Válida</CardTitle>
          </CardHeader>
          <CardContent className="text-center pb-6">
            <p className="text-gray-700 mb-6">{error}</p>
            <Button
              onClick={() => navigate("/login")} // ✅ USAMOS NAVIGATE
              className="bg-blue-600 hover:bg-blue-700"
            >
              Ir al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
          <div className="mx-auto w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-10 w-10 text-blue-600" />
          </div>
          <CardTitle className="text-2xl font-bold">¡Has sido invitado!</CardTitle>
          <p className="text-blue-100 text-sm mt-2">Configura tu cuenta para comenzar</p>
        </CardHeader>

        <CardContent className="pt-6 pb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-600">Empresa</p>
                  <p className="font-semibold text-gray-900">{invitation.empresa.nombre}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-600">Tu email</p>
                  <p className="font-medium text-gray-900">{invitation.invitation.email}</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="password"
                  type="password"
                  required
                  translate="no"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Mínimo 8 caracteres"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Contraseña <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="password_confirmation"
                  type="password"
                  required
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Repite tu contraseña"
                  value={formData.password_confirmation}
                  onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                />
              </div>
            </div>

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={validating}>
              {validating ? <Loader2 className="animate-spin mr-2" /> : <CheckCircle className="mr-2" />}
              <span>{validating ? "Creando cuenta..." : "Aceptar Invitación"}</span>
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/login")} // ✅ USAMOS NAVIGATE
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              ¿Ya tienes cuenta? Iniciar sesión
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitation;
